import React, { useEffect, createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';

const variants = {
  success: {
    icon: CheckCircle,
    className: 'bg-green-50 border-green-100 text-green-800',
    iconClass: 'text-green-500'
  },
  error: {
    icon: XCircle,
    className: 'bg-red-50 border-red-100 text-red-800',
    iconClass: 'text-red-500'
  },
  warning: {
    icon: AlertCircle,
    className: 'bg-yellow-50 border-yellow-100 text-yellow-800',
    iconClass: 'text-yellow-500'
  },
  info: {
    icon: Info,
    className: 'bg-blue-50 border-blue-100 text-blue-800',
    iconClass: 'text-blue-500'
  }
};

const ToastContext = createContext(null);

export const Toast = ({ 
  message, 
  type = 'info', 
  onClose, 
  duration = 3000,
  position = 'bottom-right',
  showCloseButton = true,
  title
}) => {
  const variant = variants[type];
  
  useEffect(() => {
    if (duration) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  const positionClasses = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4'
  };

  const Icon = variant.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 20, scale: 0.95 }}
        transition={{ type: 'spring', stiffness: 400, damping: 30 }}
        className={`
          fixed ${positionClasses[position]} 
          flex items-start 
          border rounded-lg shadow-lg 
          px-4 py-3 min-w-[300px] max-w-md 
          z-50 ${variant.className}
        `}
      >
        <div className="flex items-start flex-1">
          <Icon className={`h-5 w-5 ${variant.iconClass} mt-0.5 mr-3 flex-shrink-0`} />
          <div className="flex-1">
            {title && (
              <h4 className="font-semibold mb-1">{title}</h4>
            )}
            <p className="text-sm">{message}</p>
          </div>
        </div>

        {showCloseButton && (
          <button 
            onClick={onClose}
            className="ml-4 p-1 hover:bg-black/5 rounded-full transition-colors"
            aria-label="Close notification"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const showToast = useCallback(({ message, type = 'info', duration = 3000, title }) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts(prev => [...prev, { id, message, type, duration, title }]);

    if (duration) {
      setTimeout(() => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
      }, duration);
    }
  }, []);

  const closeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, closeToast }}>
      {children}
      <div className="fixed z-50 flex flex-col gap-2">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            title={toast.title}
            onClose={() => closeToast(toast.id)}
            duration={toast.duration}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
